import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ImagePlus, X } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ImagePreview {
  file: File;
  url: string;
}

const MAX_IMAGES = 3;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

export function FeedbackDialog({ open, onOpenChange }: Props): JSX.Element {
  const [text, setText] = useState("");
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files ?? []);
    const remaining = MAX_IMAGES - images.length;
    const accepted = files
      .filter((f) => ALLOWED_TYPES.includes(f.type))
      .slice(0, remaining);

    const newPreviews: ImagePreview[] = accepted.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
    }));
    setImages((prev) => [...prev, ...newPreviews]);
    // reset so same file can be re-selected after removal
    e.target.value = "";
  }

  function removeImage(index: number): void {
    setImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function handleSubmit(): Promise<void> {
    if (!text.trim()) return;
    setIsSubmitting(true);
    try {
      const form = new FormData();
      form.append("text", text.trim());
      form.append("pageUrl", window.location.href);
      images.forEach((img) => form.append("images", img.file));

      const res = await fetch("/api/admin/feedback", { method: "POST", body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message ?? "Failed to send feedback");
      }
      toast({ title: "Feedback sent", description: "Thanks! Your message was posted to Discord." });
      handleClose();
    } catch (err) {
      toast({
        title: "Error sending feedback",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose(): void {
    if (isSubmitting) return;
    setText("");
    images.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label htmlFor="feedback-text" className="mb-1.5 block text-sm font-medium">
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="feedback-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe the bug, issue, or idea…"
              rows={5}
              className="resize-none"
            />
          </div>

          <div>
            <Label className="mb-1.5 block text-sm font-medium">
              Screenshots{" "}
              <span className="text-muted-foreground font-normal">
                (up to {MAX_IMAGES})
              </span>
            </Label>

            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {images.map((img, i) => (
                  <div key={img.url} className="relative w-20 h-20 rounded-md overflow-hidden border bg-muted">
                    <img src={img.url} alt={`screenshot ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80 transition-colors"
                      aria-label="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {images.length < MAX_IMAGES && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
              >
                <ImagePlus className="h-4 w-4" />
                Add image
              </Button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_TYPES.join(",")}
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!text.trim() || isSubmitting}
          >
            {isSubmitting ? "Sending…" : "Send feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
